import { PageNumbers } from "@bronte/common";
import { Motivation, ContextType, ElementType } from "./enums";
import { ENVIRONMENTS } from "@bronte/viewer";

type GUID = string;

declare global {
  interface Window {
    manifest?: Manifest;
    interactiveObj?: AmbrosiaComponent;
    annotations: AmbrosiaAnnotations;
    featureFlag: LearnerFeatureFlag[];
    glossary?: Glossary;
    assets: AssetList;
    ambrosia: {
      [x: string]: unknown;
      $ambrosia: string;
      $id: string;
      pluginPayload: {
        summary: {
          pluginId: GUID;
          latestVersion: string;
        };
      };
      $workspaceId: string;
      config: Record<string, unknown>;
    };
    pageNumbers: PageNumbers;
  }
}

export interface Manifest {
  id: GUID;
  componentId: GUID; // Id of the component
  name: string;
  description: string;
  version: string;
  author: string;
  email: string;
  type: string;
  configurationSchema: string;
  searchable: Array<string>;
  views: {
    [K in ContextType]: {
      entryPoint: string;
      publicDir: string;
      contentType: string;
    };
  };
}

export interface CoursewareItemServiceParams {
  target: Target;
  context: string;
  id: GUID;
  initialState:
    | StateValue
    | {
        itemId: string;
        localRef: string;
      }[]
    | undefined;
}

export interface StateValue {
  title: string;
  description?: string;
  showTOC?: boolean;
  chapters?: CoursewareItemChild;
  lessons?: CoursewareItemChild;
  pathway?: CoursewareItemChild;
  sections?: Section[];
  [key: string]: unknown;
}

export interface RouteTarget {
  ancestry: Array<AncestryItem>;
  cohortId: GUID;
  deploymentId: GUID;
  id: GUID;
  type: ElementType;
  subTarget?: Record<string, unknown>;
}

export interface RouteSubscriber {
  coursewareId: GUID;
  listener: (target: RouteTarget) => void;
}

export interface AssetInfo {
  assetProvider: string;
  id: GUID;
  assetMediaType: string;
  ownerId: GUID;
  subscriptionId: GUID;
  hash: string;
  assetVisibility: string;
}

export interface AssetImageInfo {
  width: number;
  url: string;
  height: number;
}

export interface AssetSource {
  extra_large?: AssetImageInfo;
  large?: AssetImageInfo;
  original?: AssetImageInfo;
  medium?: AssetImageInfo;
  small?: AssetImageInfo;
  thumb?: AssetImageInfo;
}

export interface AssetMetadata {
  originalAssetName?: string;
  altText?: string;
  height?: string;
}

export interface Asset {
  title?: string;
  urn: GUID;
  asset: AssetInfo;
  source: AssetSource;
  metadata: AssetMetadata;
}

export interface Section {
  rows: SectionRow[];
}

export interface SectionRow {
  columns: SectionColumn[];
}

export interface SectionColumn {
  stageElements: StageElement[];
}

export interface StageElement {
  config?: Record<string, unknown>;
  type?: string;
}

export interface CoursewareItemChild {
  $ambrosia: string;
  $id: GUID;
  id?: GUID;
  children: AmbrosiaCoursewareItem[];
}

export type AmbrosiaAnnotations = {
  [key: GUID]: AmbrosiaAnnotation; // key here is the annotation id.
};
export interface AmbrosiaAnnotation {
  tid: GUID; // target id
  rid: GUID; // elementRef
}

export type Annotations = Array<Annotation>;

export type Annotation = {
  id: GUID;
  type?: ElementType;
  motivation?: Motivation;
  subTarget?: {
    elementRef?: GUID;
  };
  target: Target;
  body: Array<AmbrosiaBody>;
};

interface AmbrosiaBody {
  purpose: Motivation;
  type?: string;
  value: string;
}

export interface AmbrosiaCoursewareItem {
  $ambrosia: string;
  $id: GUID;
  $workspaceId: GUID;
  $projectId: GUID;
  config: Record<string, any>;
  toc: TOCItem;
  localRef?: GUID;
  configSchemaVersion?: string;
  activityTheme: {
    bodyFont: string;
    titleFont: string;
    textColor: string;
    primaryColor: string;
    surfaceColor: string;
    backgroundColor: string;
    successColor: string;
    errorColor: string;
  };
  selectedThemePayload: SelectedThemePayload;
  scenarios: Array<Record<string, unknown>>;
  pluginPayload: PluginPayload;
  assets?: Array<Asset>;
  exportMetadata: ExportMetadata;
}

export interface ExportMetadata {
  exportId: string;
  startedAt: string;
  completedAt: string;
  elementsExportedCount: number;
  exportType: string;
  metadata: string;
  ancestry: AncestryItem[];
}

export interface AmbrosiaMetadata {
  cohortId: string;
  deploymentId: string;
  productId: string;
  tocLevel: TocLevel;
}

export const TOC_LEVELS = {
  twoLevel: "Two Levels",
  threeLevel: "Three Levels",
} as const;

type TocLevel = (typeof TOC_LEVELS)[keyof typeof TOC_LEVELS];

export interface AncestryItem {
  elementId: GUID;
  elementType: ElementType;
  __typename?: string;
}

export interface SelectedThemePayload {
  id: GUID;
  name: string;
  themeVariants: ThemeVariants;
}

export type ThemeVariant = {
  themeId: GUID;
  variantId: GUID;
  variantName: string;
  config: string;
  state?: string;
};

export type ThemeVariants = Array<ThemeVariant>;

export type Theme = {
  bodyFont?: string;
  titleFont?: string;
  textColor?: string;
  primaryColor?: string;
  surfaceColor?: string;
  backgroundColor?: string;
  successColor?: string;
  errorColor?: string;
  css?: string;
};

export type AmbrosiaComponent = Pick<
  AmbrosiaCoursewareItem,
  "$ambrosia" | "$id" | "configSchemaVersion" | "config" | "pluginPayload" | "localRef" | "assets"
>;

export interface StateSubscription {
  type: string;
  subscriptionId: GUID;
  unsubscribe: () => void;
}

export interface PluginPayload {
  pluginRepositoryPath: string;
  summary: {
    pluginId: GUID;
    name: string;
    description: string;
    type: string;
    latestVersion: string;
    subscriptionId: string;
    createdAt: string;
    updatedAt: string;
    tags: string[];
    publishMode: string;
  };
}

export interface BindingInfo {
  path: string[];
  sourceId: GUID;
  context: string[];
}

export interface BindingSubscription {
  subscriptionPath: string[];
  coursewareId: GUID;
  path: string[];
}

export interface Target {
  id: GUID;
  type?: ElementType;
  ancestry?: RouteTarget["ancestry"];
  deploymentId?: GUID;
  cohortId?: GUID;
  subTarget: {
    elementRef?: GUID;
  };
}

export type Urn = string;
export interface LearnerFeatureFlag {
  key: string;
  name?: string;
  description?: string;
  active: boolean;
}

export interface GlossaryItem {
  key: string;
  value: string;
  id: string;
}

export interface Glossary {
  ancestry: string[];
  data: GlossaryItem[];
}

export interface TOCItem {
  id: string;
  type: string;
  title: string;
  children: TOCItem[];
  playOrder?: number;
}

export interface TOCMapItem {
  isChapter?: boolean;
  isLesson?: boolean;
  isScreen?: boolean;
  elementToRoute: string;
  screens: string[];
}

export interface AssetList {
  [key: string]: string[];
}

export interface Cohort {
  isMobile: boolean;
  isOnline: boolean;
  piToken: string;
  piUserId: string;
  env: ENVIRONMENTS | undefined;
  packagePath: string | undefined;
}

export type SubscriptionCallback = (isOnline: boolean) => void;

export type NetworkStatusSubscription = {
  subscriptionId: string;
  unsubscribe: () => void;
};
